if (!customElements.get("arc-compatibility-filtering")) {
  class ARCCompatibilityFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.brandSelect = this.querySelector("select.filter-brand");
      this.modelSelect = this.querySelector("select.filter-model");
      this.yearSelect = this.querySelector("select.filter-year");

      this.compatibilityData = {};
      this.products = [];
      this.resultsContainer = null;
      this.isLoading = false;

      this.init();
    }

    async init() {
      this.setupEventListeners();
      this.createResultsContainer();
      await this.fetchCompatibilityData();
      this.populateBrands();
    }

    setupEventListeners() {
      this.form?.addEventListener("submit", this.onSubmit.bind(this));

      this.brandSelect?.addEventListener(
        "change",
        this.onBrandChange.bind(this),
      );

      this.modelSelect?.addEventListener(
        "change",
        this.onModelChange.bind(this),
      );

      this.yearSelect?.addEventListener(
        "change",
        this.onYearChange.bind(this),
      );
    }

    createResultsContainer() {
      this.resultsContainer = document.createElement("div");
      this.resultsContainer.className = "arc-bike-finder-results";
      this.resultsContainer.setAttribute("aria-live", "polite");

      this.resultsContainer.style.backgroundColor = "#ffffff";
      this.resultsContainer.style.color = "#111111";
      this.resultsContainer.style.width = "100%";

      /*
       * Creates a visible black separation area between
       * the bike selector and the product results.
       */
      this.resultsContainer.style.borderTop =
        "40px solid #0b0b0b";

      /*
       * Controls the white spacing inside the results area.
       */
      this.resultsContainer.style.padding = "40px 0 60px";

      this.resultsContainer.style.display = "none";

      this.insertAdjacentElement(
        "afterend",
        this.resultsContainer,
      );
    }

    async fetchCompatibilityData() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.toggleLoading(true);

      try {
        const allProducts = await this.fetchAllProducts();

        this.products = allProducts
          .map(({ node }) => node)
          .filter((product) => product.compatibilities?.value);

        this.compatibilityData =
          this.buildCompatibilityObject(allProducts);
      } catch (error) {
        console.error(
          "Error fetching compatibility data:",
          error,
        );

        this.showMessage(
          "We could not load the bike fitment data. Please refresh the page and try again.",
        );
      } finally {
        this.isLoading = false;
        this.toggleLoading(false);
      }
    }

    async fetchAllProducts() {
      const products = [];
      let hasNextPage = true;
      let cursor = null;

      const query = `
        query GetProductsWithCompatibility($cursor: String) {
          products(first: 250, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                availableForSale

                featuredImage {
                  url
                  altText
                }

                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }

                compatibilities: metafield(
                  namespace: "custom"
                  key: "compatibilities"
                ) {
                  value
                  type
                }
              }
            }
          }
        }
      `;

      while (hasNextPage) {
        const response = await fetch(
          "https://arc-levers.myshopify.com/api/2024-01/graphql.json",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                "817182a77a624257b6d8fbcbfbdbeb2f",
            },
            body: JSON.stringify({
              query,
              variables: { cursor },
            }),
          },
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error! Status: ${response.status}`,
          );
        }

        const data = await response.json();

        if (data.errors?.length) {
          throw new Error(data.errors[0].message);
        }

        const productData = data.data.products;

        products.push(...productData.edges);

        hasNextPage =
          productData.pageInfo.hasNextPage;

        cursor =
          productData.pageInfo.endCursor;
      }

      return products;
    }

    buildCompatibilityObject(products) {
      const compatibility = {};

      products.forEach(({ node: product }) => {
        if (!product.compatibilities?.value) return;

        try {
          const compatibilitiesArray = JSON.parse(
            product.compatibilities.value,
          );

          compatibilitiesArray.forEach((item) => {
            const brand = String(
              item.brand || "",
            ).trim();

            const model = String(
              item.model || "",
            ).trim();

            const years = Array.isArray(item.years)
              ? item.years
              : [];

            if (!brand || !model) return;

            if (!compatibility[brand]) {
              compatibility[brand] = {};
            }

            if (!compatibility[brand][model]) {
              compatibility[brand][model] =
                new Set();
            }

            years.forEach((year) => {
              compatibility[brand][model].add(
                String(year),
              );
            });
          });
        } catch (error) {
          console.error(
            `Error parsing compatibilities for product ${product.title}:`,
            error,
          );
        }
      });

      Object.keys(compatibility).forEach(
        (brand) => {
          Object.keys(
            compatibility[brand],
          ).forEach((model) => {
            compatibility[brand][model] =
              Array.from(
                compatibility[brand][model],
              ).sort((a, b) => {
                if (a === "ALL") return -1;
                if (b === "ALL") return 1;

                return Number(b) - Number(a);
              });
          });
        },
      );

      return compatibility;
    }

    populateBrands() {
      if (!this.brandSelect) return;

      this.clearSelect(this.brandSelect);

      const brands = Object.keys(
        this.compatibilityData,
      ).sort((a, b) => a.localeCompare(b));

      brands.forEach((brand) => {
        const option =
          document.createElement("option");

        option.value = brand;
        option.textContent = brand;

        this.brandSelect.appendChild(option);
      });

      this.brandSelect.disabled = false;

      this.brandSelect
        .closest(".form-group")
        ?.classList.remove("disabled");
    }

    onBrandChange(event) {
      const selectedBrand =
        event.target.value;

      this.clearResults();
      this.clearSelect(this.modelSelect);
      this.clearSelect(this.yearSelect);

      this.modelSelect.disabled = true;
      this.yearSelect.disabled = true;
      this.button.disabled = true;

      this.modelSelect
        .closest(".form-group")
        ?.classList.add("disabled");

      this.yearSelect
        .closest(".form-group")
        ?.classList.add("disabled");

      if (!selectedBrand) return;

      const models = Object.keys(
        this.compatibilityData[
          selectedBrand
        ] || {},
      ).sort((a, b) => a.localeCompare(b));

      models.forEach((model) => {
        const option =
          document.createElement("option");

        option.value = model;
        option.textContent = model;

        this.modelSelect.appendChild(option);
      });

      this.modelSelect.disabled = false;

      this.modelSelect
        .closest(".form-group")
        ?.classList.remove("disabled");
    }

    onModelChange(event) {
      const selectedBrand =
        this.brandSelect.value;

      const selectedModel =
        event.target.value;

      this.clearResults();
      this.clearSelect(this.yearSelect);

      this.yearSelect.disabled = true;
      this.button.disabled = true;

      this.yearSelect
        .closest(".form-group")
        ?.classList.add("disabled");

      if (!selectedModel) return;

      const years =
        this.compatibilityData[
          selectedBrand
        ]?.[selectedModel] || [];

      years.forEach((year) => {
        const option =
          document.createElement("option");

        option.value = year;
        option.textContent = year;

        this.yearSelect.appendChild(option);
      });

      this.yearSelect.disabled = false;

      this.yearSelect
        .closest(".form-group")
        ?.classList.remove("disabled");
    }

    onYearChange(event) {
      this.clearResults();

      this.button.disabled =
        !event.target.value;
    }

    onSubmit(event) {
      event.preventDefault();

      const brand =
        this.brandSelect?.value?.trim();

      const model =
        this.modelSelect?.value?.trim();

      const year =
        this.yearSelect?.value?.trim();

      if (!brand || !model || !year) return;

      this.toggleLoading(true);

      try {
        const matchingProducts =
          this.findMatchingProducts(
            brand,
            model,
            year,
          );

        this.renderResults(
          matchingProducts,
          {
            brand,
            model,
            year,
          },
        );
      } catch (error) {
        console.error(
          "Error filtering products:",
          error,
        );

        this.showMessage(
          "Something went wrong while finding your products. Please try again.",
        );
      } finally {
        this.toggleLoading(false);
      }
    }

    findMatchingProducts(
      selectedBrand,
      selectedModel,
      selectedYear,
    ) {
      return this.products.filter(
        (product) => {
          if (
            !product.compatibilities?.value
          ) {
            return false;
          }

          try {
            const entries = JSON.parse(
              product.compatibilities.value,
            );

            return entries.some((entry) => {
              const brand = String(
                entry.brand || "",
              ).trim();

              const model = String(
                entry.model || "",
              ).trim();

              const years = Array.isArray(
                entry.years,
              )
                ? entry.years.map((year) =>
                    String(year).trim(),
                  )
                : [];

              const brandMatches =
                brand === selectedBrand;

              const modelMatches =
                model === selectedModel;

              const yearMatches =
                years.includes(selectedYear) ||
                years.includes("ALL");

              return (
                brandMatches &&
                modelMatches &&
                yearMatches
              );
            });
          } catch (error) {
            console.error(
              `Error checking product ${product.title}:`,
              error,
            );

            return false;
          }
        },
      );
    }

    renderResults(products, selection) {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML = "";

      this.resultsContainer.style.display =
        "block";

      const wrapper =
        document.createElement("div");

      wrapper.style.maxWidth = "1400px";
      wrapper.style.margin = "0 auto";
      wrapper.style.padding = "0 24px";

      const heading =
        document.createElement("h2");

      heading.textContent =
        "PRODUCTS FOR YOUR BIKE";

      heading.style.color = "#111111";
      heading.style.textAlign = "center";
      heading.style.margin = "0 0 8px";
      heading.style.fontSize = "30px";
      heading.style.fontWeight = "700";

      const fitment =
        document.createElement("p");

      fitment.textContent =
        `${selection.brand} • ${selection.model} • ${selection.year}`;

      fitment.style.color = "#555555";
      fitment.style.textAlign = "center";
      fitment.style.margin = "0 0 32px";
      fitment.style.fontSize = "16px";

      wrapper.appendChild(heading);
      wrapper.appendChild(fitment);

      if (!products.length) {
        const noResults =
          document.createElement("p");

        noResults.textContent =
          "No compatible products were found for this selection.";

        noResults.style.color = "#111111";
        noResults.style.textAlign = "center";
        noResults.style.padding = "32px 0";

        wrapper.appendChild(noResults);

        this.resultsContainer.appendChild(
          wrapper,
        );

        this.scrollToResults();

        return;
      }

      const grid =
        document.createElement("div");

      grid.style.display = "grid";

      grid.style.gridTemplateColumns =
        "repeat(auto-fit, minmax(240px, 1fr))";

      grid.style.gap = "24px";
      grid.style.alignItems = "stretch";

      products.forEach((product) => {
        grid.appendChild(
          this.createProductCard(product),
        );
      });

      wrapper.appendChild(grid);

      this.resultsContainer.appendChild(
        wrapper,
      );

      this.scrollToResults();
    }

    createProductCard(product) {
      const card =
        document.createElement("article");

      card.style.display = "flex";
      card.style.flexDirection = "column";
      card.style.textAlign = "center";
      card.style.backgroundColor = "#ffffff";
      card.style.color = "#111111";
      card.style.border =
        "1px solid #dddddd";
      card.style.borderRadius = "4px";
      card.style.overflow = "hidden";
      card.style.minHeight = "100%";

      const productUrl =
        `${window.location.origin}/products/${product.handle}`;

      const imageLink =
        document.createElement("a");

      imageLink.href = productUrl;
      imageLink.style.display = "block";
      imageLink.style.aspectRatio = "1 / 1";
      imageLink.style.backgroundColor =
        "#ffffff";
      imageLink.style.padding = "12px";

      if (product.featuredImage?.url) {
        const image =
          document.createElement("img");

        image.src =
          product.featuredImage.url;

        image.alt =
          product.featuredImage.altText ||
          product.title ||
          "ARC product";

        image.loading = "lazy";
        image.style.width = "100%";
        image.style.height = "100%";
        image.style.objectFit = "contain";
        image.style.display = "block";

        imageLink.appendChild(image);
      }

      const content =
        document.createElement("div");

      content.style.display = "flex";
      content.style.flexDirection = "column";
      content.style.flexGrow = "1";
      content.style.padding = "18px";

      const titleLink =
        document.createElement("a");

      titleLink.href = productUrl;
      titleLink.textContent = product.title;
      titleLink.style.color = "#111111";
      titleLink.style.textDecoration = "none";
      titleLink.style.fontWeight = "600";
      titleLink.style.marginBottom = "10px";

      const price =
        document.createElement("div");

      const amount =
        product.priceRange
          ?.minVariantPrice?.amount;

      const currencyCode =
        product.priceRange
          ?.minVariantPrice
          ?.currencyCode || "USD";

      if (amount) {
        price.textContent =
          this.formatMoney(
            amount,
            currencyCode,
          );
      }

      price.style.color = "#111111";
      price.style.marginBottom = "16px";

      const productButton =
        document.createElement("a");

      productButton.href = productUrl;

      productButton.textContent =
        product.availableForSale
          ? "SELECT OPTIONS"
          : "VIEW PRODUCT";

      productButton.style.display = "block";
      productButton.style.marginTop = "auto";
      productButton.style.padding =
        "14px 18px";
      productButton.style.backgroundColor =
        "#0b0b0b";
      productButton.style.color = "#ffffff";
      productButton.style.textDecoration =
        "none";
      productButton.style.fontWeight = "600";
      productButton.style.textTransform =
        "uppercase";

      content.appendChild(titleLink);
      content.appendChild(price);
      content.appendChild(productButton);

      card.appendChild(imageLink);
      card.appendChild(content);

      return card;
    }

    formatMoney(amount, currencyCode) {
      const numericAmount = Number(amount);

      if (Number.isNaN(numericAmount)) {
        return "";
      }

      try {
        return new Intl.NumberFormat(
          "en-US",
          {
            style: "currency",
            currency: currencyCode,
          },
        ).format(numericAmount);
      } catch (error) {
        return `$${numericAmount.toFixed(2)}`;
      }
    }

    showMessage(message) {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML = "";

      this.resultsContainer.style.display =
        "block";

      const paragraph =
        document.createElement("p");

      paragraph.textContent = message;
      paragraph.style.color = "#111111";
      paragraph.style.backgroundColor =
        "#ffffff";
      paragraph.style.textAlign = "center";
      paragraph.style.padding =
        "40px 20px";
      paragraph.style.margin = "0";

      this.resultsContainer.appendChild(
        paragraph,
      );
    }

    clearResults() {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML = "";

      this.resultsContainer.style.display =
        "none";
    }

    scrollToResults() {
      this.resultsContainer?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    clearSelect(select) {
      if (!select) return;

      const options =
        select.querySelectorAll(
          "option:not(.default)",
        );

      options.forEach((option) => {
        option.remove();
      });

      const defaultOption =
        select.querySelector(
          "option.default",
        );

      if (defaultOption) {
        defaultOption.selected = true;
      }
    }

    toggleLoading(isLoading = true) {
      if (isLoading) {
        this.classList.add("is-loading");

        this.button?.classList.add(
          "loading",
        );

        if (this.button) {
          this.button.disabled = true;
        }
      } else {
        this.classList.remove(
          "is-loading",
        );

        this.button?.classList.remove(
          "loading",
        );

        if (
          this.button &&
          this.yearSelect?.value
        ) {
          this.button.disabled = false;
        }
      }
    }
  }

  customElements.define(
    "arc-compatibility-filtering",
    ARCCompatibilityFiltering,
  );
}